import useSWR, { mutate } from 'swr';
import { getBrowserClient } from '@/lib/integrations/supabase/client';
import { getInventoryProducts, getLowStockProducts } from '@/lib/features/data/inventory';
import type { InventoryProduct, InventoryLog, InventoryLogWithActor } from '@/lib/integrations/supabase/types';

// ============================================================================
// SWR Keys
// ============================================================================

const getInventoryKey = (businessId: string | null) =>
  businessId ? `inventory:${businessId}` : null;

const getInventoryProductKey = (id: string | null) =>
  id ? `inventory:product:${id}` : null;

const getInventoryLogsKey = (productId: string | null) =>
  productId ? `inventory:logs:${productId}` : null;

const getLowStockKey = (businessId: string | null) =>
  businessId ? `inventory:low-stock:${businessId}` : null;

// ============================================================================
// Fetchers
// ============================================================================

async function fetchInventoryProducts(businessId: string): Promise<InventoryProduct[]> {
  return getInventoryProducts(businessId);
}

async function fetchInventoryProduct(id: string): Promise<InventoryProduct | null> {
  const supabase = getBrowserClient();
  
  const { data, error } = await supabase
    .from('inventory_products')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }

  return data;
}

async function fetchInventoryLogs(productId: string): Promise<InventoryLogWithActor[]> {
  const supabase = getBrowserClient();

  const { data, error } = await supabase
    .from('inventory_logs')
    .select(`
      *,
      actor:profiles!inventory_logs_actor_id_fkey(*)
    `)
    .eq('product_id', productId)
    .order('created_at', { ascending: false })
    .limit(50);

  if (error) {
    throw error;
  }

  return (data || []) as InventoryLogWithActor[];
}

async function fetchLowStockProducts(businessId: string): Promise<InventoryProduct[]> {
  return getLowStockProducts(businessId);
}

// ============================================================================
// SWR Hooks
// ============================================================================

/**
 * Hook to fetch all inventory products for a business.
 */
export function useInventoryProducts(businessId: string | null) {
  return useSWR(
    getInventoryKey(businessId),
    businessId ? () => fetchInventoryProducts(businessId) : null,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 5000,
    }
  );
}

/**
 * Hook to fetch a single inventory product.
 */
export function useInventoryProduct(id: string | null) {
  return useSWR(
    getInventoryProductKey(id),
    id ? () => fetchInventoryProduct(id) : null,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 2000,
    }
  );
}

/**
 * Hook to fetch logs for a product.
 */
export function useInventoryLogs(productId: string | null) {
  return useSWR(
    getInventoryLogsKey(productId),
    productId ? () => fetchInventoryLogs(productId) : null,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 5000,
    }
  );
}

/**
 * Hook to fetch low stock products.
 */
export function useLowStockProducts(businessId: string | null) {
  return useSWR(
    getLowStockKey(businessId),
    businessId ? () => fetchLowStockProducts(businessId) : null,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 10000, // 10 seconds
    }
  );
}

// ============================================================================
// Cache Invalidation
// ============================================================================

/**
 * Invalidate all inventory data for a business.
 */
export function invalidateInventory(businessId: string) {
  mutate(getInventoryKey(businessId));
  mutate(getLowStockKey(businessId));
}

/**
 * Invalidate a specific product.
 */
export function invalidateInventoryProduct(id: string, businessId?: string) {
  mutate(getInventoryProductKey(id));
  if (businessId) {
    mutate(getInventoryKey(businessId));
    mutate(getLowStockKey(businessId));
  }
}

/**
 * Invalidate logs for a product.
 */
export function invalidateInventoryLogs(productId: string) {
  mutate(getInventoryLogsKey(productId));
}

